import React, { useState, useEffect, useRef } from 'react';
import { Tag } from '../../types/contact';
import { tagApi } from '../../services/tagApi';
import { COLD_OUTREACH_TAG_NAME, isColdOutreachTagName } from '../../constants/contactTags';
import { Button } from '../ui/Button';
import styles from './ContactFilters.module.css';

interface ContactFiltersProps {
  activeTagIds: string[];
  excludeColdInbound: boolean;
  companyFilter: string | null;
  onTagToggle: (tagId: string) => void;
  onExcludeColdInboundChange: (exclude: boolean) => void;
  onCompanyChange: (company: string | null) => void;
  onClearAll: () => void;
}

const COMPANY_DEBOUNCE_MS = 400;
export const ContactFilters: React.FC<ContactFiltersProps> = ({
  activeTagIds,
  excludeColdInbound,
  companyFilter,
  onTagToggle,
  onExcludeColdInboundChange,
  onCompanyChange,
  onClearAll,
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [companyInput, setCompanyInput] = useState(companyFilter || '');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    tagApi.getUserTags().then(result => {
      setTags(result.tags);
    }).catch(() => {
      // Silently fail - tags filter just won't be available
    });
  }, []);

  useEffect(() => {
    setCompanyInput(companyFilter || '');
  }, [companyFilter]);

  const handleCompanyInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCompanyInput(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onCompanyChange(value || null);
    }, COMPANY_DEBOUNCE_MS);
  };

  const handleTagSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tagId = e.target.value;
    if (tagId && !activeTagIds.includes(tagId)) {
      onTagToggle(tagId);
    }
    // Reset select to placeholder
    e.target.value = '';
  };

  const hasActiveFilters = excludeColdInbound || activeTagIds.length > 0 || !!companyFilter;

  const getTagName = (tagId: string): string => {
    const tag = tags.find(t => t.id === tagId);
    return tag?.name ?? tagId;
  };

  return (
    <div className={styles.filterBar}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Tags:</span>
        <select
          className={styles.tagSelect}
          onChange={handleTagSelect}
          defaultValue=""
        >
          <option value="" disabled>Add tag filter...</option>
          {tags
            .filter(t => !activeTagIds.includes(t.id))
            .filter(t => !excludeColdInbound || !isColdOutreachTagName(t.name))
            .map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
        </select>
      </div>

      {(excludeColdInbound || activeTagIds.length > 0) && (
        <div className={styles.activeTags}>
          {excludeColdInbound && (
            <span className={styles.activeTag}>
              Excluding {COLD_OUTREACH_TAG_NAME}
              <span
                className={styles.removeTag}
                onClick={() => onExcludeColdInboundChange(false)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onExcludeColdInboundChange(false);
                  }
                }}
              >
                ×
              </span>
            </span>
          )}
          {activeTagIds.map(tagId => (
            <span key={tagId} className={styles.activeTag}>
              {getTagName(tagId)}
              <span
                className={styles.removeTag}
                onClick={() => onTagToggle(tagId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onTagToggle(tagId);
                  }
                }}
              >
                ×
              </span>
            </span>
          ))}
        </div>
      )}

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Company:</span>
        <input
          type="text"
          className={styles.companyInput}
          placeholder="Filter by company..."
          value={companyInput}
          onChange={handleCompanyInputChange}
        />
      </div>

      {hasActiveFilters && (
        <div className={styles.clearAll}>
          <Button variant="ghost" size="sm" onClick={onClearAll}>
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContactFilters;
